/** @format */

import type { FormItemRule } from 'element-plus';

export type Category = 'container' | 'field' | 'other'; // 组件属性 容器/字段
export type Cols = '6' | '8' | '12' | '24'; // 组件占宽
export type IsRequired = '0' | '1' | '2'; // 是否必填，依次为只读，选填，必填
export type LinkConfigType = { code: string; link: string };

// 支持的组件类型
export type WidgetCPTType =
	| 'collapse'
	| 'tabs'
	| 'card'
	| 'input'
	| 'input-number'
	| 'select'
	| 'radio'
	| 'checkbox'
	| 'switch'
	| 'inputarea'
	| 'datetime'
	| 'placeholder'
	| 'timeType'
	| 'params'
	| 'defaultValue'
	| 'color'
	| 'rate'
	| 'slider'
	| 'tags'
	| 'button'
	| 'cascader'
	| 'cascader-params'
	| 'link'
	| 'iframe'
	| 'tabs-params'
	| 'script'
	| 'richText'
	| 'upload'
	| 'image'
	| 'staticText'
	| 'divider'
	| 'user'
	| 'department'
	| 'role'
	| 'linkConfig'
	| 'esign'
	| 'video'
	| 'subTable'
	| 'actionButton'
	| 'userRangeDept'
	| 'desensitize'
	| 'tree-select'
	| 'groupRange'
	| 'userRangeDept';

// --------------------- 组件配置 ---------------------

// 整体属性
export type Widget<T> = {
	title: string; // 组件名称
	children: T[];
};

// 组件静态属性
export type WidgetState = {
	category: Category; // 组件属性 容器/字段
	type: WidgetCPTType; // 组件类型
	code: string; // 组件code
	icon?: string; // 组件icon
	pcProps: WidgetConfigType; // 组件配置
};

// 容器属性
export interface Container extends WidgetState {
	category: 'container';
	children: FieldsType[]; // 容器子集，默认为空
	isActive?: boolean; // 当前是否命中，给tabs使用
}

// 基础字段属性
export interface BasicField extends WidgetState {
	category: 'field' | 'other';
}

// 所有组件类型
export type FieldsType = Container | BasicField;

// --------------------- 属性配置 ---------------------

// 字段配置属性对象实例中pcProps
export interface WidgetConfigOption {
	label: string;
	value: string;
	[key: string]: any;
}

// 组件配置支持的属性
export type WidgetConfigType = {
	code?: string; // 字段code
	label?: string; // 字段label
	cols: Cols; // 字段占宽比
	hidden?: boolean; // 是否隐藏
	params?: WidgetConfigOption[]; // 下拉属性
	cascaderParams?: WidgetConfigOption[];
	tabsParams?: string; // 不重要，用来指定有这个参数
	isRequired?: IsRequired; // 是否必填
	max?: number; // 最大值
	min?: number; // 最小值
	maxlength?: number; // 最大长度
	timeType?: string; // 时间类型
	multiple?: boolean; // 是否多选
	defaultValue?: any; // 默认值
	isPassword?: boolean; // 是否为密码类型
	showPassword?: boolean; // 展示密码图标
	placeholder?: string; // 占位文本
	requiredMsg?: string;
	validation?: string;
	validErrorMsg?: string;
	precision?: number;
	allowCreate?: boolean;
	multipleLimit?: number;
	isNotfilterable?: boolean;
	isRemote?: boolean;
	step?: number;
	isButton?: boolean;
	isTag?: boolean;
	border?: boolean;
	rows?: number;
	activeColor?: string;
	inactiveColor?: string;
	activeText?: string;
	inactiveText?: string;
	inlinePrompt?: boolean;
	maxThreshold?: number;
	allowHalf?: boolean;
	showText?: boolean;
	rateTexts?: string;
	showScore?: boolean;
	scoreTemplate?: string;
	showStops?: boolean;
	buttonType?: 'primary' | 'success' | 'warning' | 'danger' | 'info';
	position?: 'left' | 'center' | 'right';
	showAllLevels?: boolean;
	checkStrictly?: boolean;
	separator?: string;
	isHiddenLabel?: boolean;
	isLink?: boolean;
	isRedirect?: boolean;
	toUrl?: string;
	linkUrl?: string;
	height?: string;
	width?: string;
	onRemoteScript?: string;
	onChangeScript?: string;
	onClickScript?: string;
	onDoubleClickScript?: string;
	onBlurScript?: string;
	onFoucsScript?: string;
	onTabChangeScript?: string;
	defaultOpen?: boolean;
	accept?: string;
	acceptImg?: string;
	maxUpLength?: number;
	acceptVideo?: string;
	showTip?: boolean;
	maxSize?: number;
	defaultValueType?: WidgetCPTType;
	labelWidth?: number;
	size?: 'large' | 'default' | 'small' | '';
	direction?: 'horizontal' | 'vertical';
	linkConfig?: LinkConfigType[];
	isTimeRange?: boolean;
	isTimestamp?: boolean;
	isTenTimestamp?: boolean;
	showDisabledText?: boolean;
	paramsType?: string;
	citeType?: string;
	backgroundColor?: string;
	fontColor?: string;
	labelColor?: string;
	borderRadius?: number;
	isCurrentUser?: boolean;
	userRange?: string;
	userRangeDept?: any;
	helpTips?: string;
	showTable?: boolean;
	replaceFile?: boolean;
	actionButton?: {
		label: string;
		code: string;
		script: string;
	}[];
	rowKey?: string;
	templateFile?: string;
	showType?: string;
	splitFlag?: string;
	desensitize?: any;
};

export interface FormItemProps {
	rules?: FormItemRule[];
	[key: string]: any;
}

// 字段配置属性对象实例
export interface WidgetConfig {
	type: WidgetCPTType;
	pcProps: Partial<WidgetConfigType> & { [key: string]: any };
	isBind?: boolean;
	parentTitle: number;
	formItemProps?: FormItemProps | ((a: any) => FormItemProps);
}

// 字段配置属性对象
export type WidgetConfigs = {
	[K in keyof WidgetConfigType]: WidgetConfig;
};

// 在组件设置中分组展示
export enum INIT_FIELDS_ATTRIBUTE_TITLE_ENUM {
	'常见属性',
	'选项设置',
	'高级属性',
	'校验规则',
	'标签配置项',
	'功能按钮',
	'事件',
	'样式属性',
}
export interface BaseConfigVal {
	FIELDS: Widget<FieldsType>[];
	FIELDS_ATTRIBUTE: WidgetConfigs;
	FORM_ATTRIBUTE: Record<string, WidgetConfig>;
	DONT_SHOW_DEFAULT_FIELDS: WidgetCPTType[];
	FIELDS_ATTRIBUTE_TITLE_ENUM: typeof INIT_FIELDS_ATTRIBUTE_TITLE_ENUM;
	FIELDS_COMPONENTS: Record<string, any>;
	DONT_SHOW_MOBILE_FIELDS: [keyof WidgetConfigType];
}

export type SetBaseConfig = (a: Partial<BaseConfigVal>) => Promise<Partial<BaseConfigVal>> | Partial<BaseConfigVal>;

export interface ScriptFuncItemType {
	acProps?: { meta: string; value: string };
	script: Function;
}

export type ScriptFuncType = Record<string, Record<string, ScriptFuncItemType>> | Function;

export type TemplateType = {
	label: string;
	code: string;
	widgetDatas?: FieldsType[];
	children?: TemplateType[];
};
